import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, TrendingUp, X, Square, RotateCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminScoreNonDivQualityButton — Round 95z (Jun 2026).
 *
 * Section 3 of the Non-Dividend Stock Expansion. Runs the
 * growth-focused quality scorer over every `pays_dividend=false`
 * row in `stock_cache` and persists `non_dividend_quality_score`
 * (0-100) + `non_dividend_quality_tier` (`GROWTH-S` → `SPECULATIVE`).
 *
 * Pulls bulk Massive Ratios + Income Statements (annual) and
 * computes 3yr revenue/EPS CAGR locally. Runtime ~5-7 min for the
 * 4,393 non-div universe.
 *
 * Mirror of AdminHydrateNonDividendUniverseButton's UX — progress
 * bar + stop control + last-run summary card.
 */
export default function AdminScoreNonDivQualityButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [resettingCooldown, setResettingCooldown] = useState(false);
  const pollRef = useRef(null);
  // Round 97a — Track previous running state across polls so we can
  // fire a one-shot completion toast the moment in_progress flips
  // false (the old code tried to toast at POST time, which fired
  // milliseconds after kickoff — long before the scorer finished).
  const prevRunningRef = useRef(false);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    refreshStatus();
  }, [user]);

  const refreshStatus = async () => {
    try {
      const { data } = await axios.get(
        `${API}/admin/score-non-div-quality/status`,
        { withCredentials: true }
      );
      setStatus(data);
      const isRunning = !!data?.in_progress;
      // Detect the running → finished transition and surface a toast.
      if (prevRunningRef.current && !isRunning) {
        const ls = data?.last_summary || data?.last_result;
        if (ls && (ls.scored != null || ls.targets != null)) {
          toast.success(
            `Scoring complete — scored ${ls.scored ?? '—'} of ${ls.targets ?? '—'}`,
            {
              description: `Unscored: ${ls.unscored ?? '—'} · Elapsed: ${ls.elapsed_sec ?? '—'}s`,
            }
          );
        }
      }
      prevRunningRef.current = isRunning;
      setRunning(isRunning);
    } catch {
      // silent — status polling failures shouldn't break the modal.
    }
  };

  useEffect(() => {
    if (!running) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }
    pollRef.current = setInterval(refreshStatus, 3000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [running]);

  if (user?.role !== 'admin') return null;

  const startScoring = async () => {
    setStarting(true);
    setRunning(true);
    try {
      // Round 97a (Feb 2026) — The /fire endpoint is already
      // fire-and-forget on the backend: it spawns the scorer via
      // `asyncio.create_task` and returns `{started, run_id, label}`
      // in milliseconds. The old code awaited the POST AND then
      // showed a misleading "Scoring complete — scored — of —"
      // toast, plus immediately flipped `running` back to false in
      // the finally block — which killed the polling loop the user
      // expected to watch the progress bar advance.
      //
      // The clean fix: kick off the job, show a "Scoring started"
      // toast, refresh status once so polling picks up
      // `in_progress=true`, and let the useEffect handle the rest.
      // The 3-second poller drives the modal until the scorer ends.
      const { data } = await axios.post(
        `${API}/admin/score-non-div-quality/fire`,
        null,
        // 15s is plenty for the kickoff handshake. The old 20-minute
        // timeout was a red herring — production CDNs were killing
        // the connection long before then and the thrown error
        // bubbled up through a render path that black-screened the
        // app (no error boundary). Both root causes are now fixed.
        { withCredentials: true, timeout: 15000 }
      );
      if (data?.started === false) {
        toast.error('Could not start scoring', {
          description: data?.detail || 'Backend rejected the kickoff.',
        });
        setRunning(false);
      } else {
        toast.info('Scoring started', {
          description: `Run ID ${data?.run_id || '—'}. Live progress will update below.`,
        });
      }
      await refreshStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || err?.message || 'Unknown error';
      toast.error('Could not start scoring', { description: msg });
      setRunning(false);
    } finally {
      setStarting(false);
    }
  };

  const stopScoring = async () => {
    try {
      await axios.post(
        `${API}/admin/score-non-div-quality/stop`,
        null,
        { withCredentials: true }
      );
      toast.info('Stop signal sent — finishes current 200-ticker checkpoint then halts');
    } catch (err) {
      toast.error('Stop failed', { description: err?.message });
    }
  };

  // Round 97a (Feb 2026) — Reset the 7-day cooldown so an admin can
  // manually fire the scorer again without waiting (or curl).
  const resetCooldown = async () => {
    setResettingCooldown(true);
    try {
      await axios.post(
        `${API}/admin/score-non-div-quality/reset-cooldown`,
        null,
        { withCredentials: true, timeout: 15000 }
      );
      toast.success('Cooldown cleared', {
        description: 'You can fire Start Scoring again now.',
      });
      await refreshStatus();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = (typeof detail === 'string' && detail) || err?.message || 'Unknown error';
      toast.error('Reset failed', { description: msg });
    } finally {
      setResettingCooldown(false);
    }
  };

  const lastResult = status?.last_summary || status?.last_result;
  const progress = status?.in_progress_total
    ? Math.round((status.in_progress_processed / status.in_progress_total) * 100)
    : 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-score-non-div-quality-button"
        className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-teal-500/15 hover:bg-teal-500/25 text-teal-900 dark:text-teal-200 border border-teal-500/40 transition-colors"
        title="Score non-dividend tickers on growth + leverage + valuation. Powers the upcoming Non-Dividend column set."
      >
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
        Score non-div quality
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setOpen(false)}
          data-testid="admin-score-non-div-quality-modal"
        >
          <div
            className="relative w-full max-w-xl bg-card border border-teal-500/40 rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/60">
              <div>
                <h2 className="text-sm font-bold">Non-Dividend Quality Scorer</h2>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  Growth + leverage + valuation rubric. 100-pt total, 5 tiers
                  (GROWTH-S → SPECULATIVE).
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="admin-score-non-div-quality-close"
                className="p-1 rounded hover:bg-muted/40"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {running ? (
                <div className="space-y-2" data-testid="admin-score-non-div-progress">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-mono">
                      {status?.in_progress_processed ?? 0} / {status?.in_progress_total ?? '?'}
                    </span>
                    <span className="font-bold text-teal-300">{progress}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                    <div
                      className="h-full bg-teal-500 transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <button
                    onClick={stopScoring}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 border border-rose-500/40"
                    data-testid="admin-score-non-div-stop"
                  >
                    <Square className="w-4 h-4" />
                    Stop after current checkpoint
                  </button>
                </div>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground space-y-1.5">
                    <p>Scores every <code className="text-foreground bg-muted/30 px-1 rounded">pays_dividend=false</code> row using bulk Massive Ratios + Income Statements (annual).</p>
                    <ul className="list-disc list-inside text-[11px] space-y-0.5 ml-2">
                      <li>Revenue 3yr CAGR (25 pts)</li>
                      <li>EPS 3yr CAGR (20 pts)</li>
                      <li>ROE (15 pts) · FCF positive (15 pts)</li>
                      <li>D/E ratio (10 pts) · P/E reasonable (10 pts)</li>
                      <li>Gross margin (5 pts)</li>
                    </ul>
                  </div>
                  <button
                    onClick={startScoring}
                    disabled={starting}
                    data-testid="admin-score-non-div-start"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-teal-500/20 hover:bg-teal-500/30 text-teal-200 border border-teal-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                    Start scoring
                  </button>
                </>
              )}

              {lastResult && (
                <div className="rounded-md border border-border/60 bg-muted/20 p-3 space-y-1" data-testid="admin-score-non-div-last-result">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Last run</p>
                  <div className="text-xs font-mono space-y-0.5">
                    <div>Targets: <span className="text-foreground font-bold">{lastResult.targets}</span></div>
                    <div>Scored: <span className="text-emerald-300 font-bold">{lastResult.scored}</span></div>
                    <div>Unscored: <span className={lastResult.unscored > 0 ? 'text-amber-300 font-bold' : 'text-muted-foreground'}>{lastResult.unscored}</span></div>
                    <div>Elapsed: <span className="text-foreground">{lastResult.elapsed_sec}s</span></div>
                    {status?.last_completed_at && (
                      <div className="text-[10px] text-muted-foreground mt-1">
                        Completed: {new Date(status.last_completed_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                  {/* Round 97a (Feb 2026) — Admin-only "Reset cooldown"
                      escape hatch. The scorer enforces a 7-day cooldown
                      and the only way to override used to be a curl
                      against /admin/score-non-div-quality/reset-cooldown.
                      Inline button replaces the curl ritual. */}
                  {!running && (
                    <button
                      type="button"
                      onClick={resetCooldown}
                      disabled={resettingCooldown}
                      data-testid="admin-score-non-div-reset-cooldown"
                      className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded text-[11px] font-medium bg-amber-500/15 hover:bg-amber-500/25 text-amber-200 border border-amber-500/40 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resettingCooldown ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                      Reset cooldown
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
